import React, { useState, useEffect } from 'react';
import { LineChart, Line, PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, CheckCircle, XCircle, Clock, TrendingUp } from 'lucide-react';
import api from '../../services/api';

const AppointmentAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/analytics/appointments');
      setAnalytics(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load appointment analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
        <button onClick={fetchAnalytics} className="ml-4 text-sm underline hover:no-underline">
          Retry
        </button>
      </div>
    );
  }

  // Status distribution for pie chart
  const statusData = [
    { name: 'Completed', value: analytics?.status_distribution?.completed || 0, color: '#10b981' },
    { name: 'Scheduled', value: analytics?.status_distribution?.scheduled || 0, color: '#3b82f6' },
    { name: 'Missed', value: analytics?.status_distribution?.missed || 0, color: '#ef4444' },
    { name: 'Cancelled', value: analytics?.status_distribution?.cancelled || 0, color: '#6b7280' }
  ];

  // Monthly appointments
  const monthlyData = analytics?.monthly_appointments || [];

  // Peak hours data
  const peakHoursData = analytics?.hour_distribution ? 
    Object.entries(analytics.hour_distribution)
      .map(([hour, count]) => ({
        hour: `${hour}:00`,
        count
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => parseInt(a.hour) - parseInt(b.hour)) : [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Appointments</p>
              <p className="text-3xl font-bold mt-2">{analytics?.total_appointments || 0}</p>
            </div>
            <Calendar className="w-12 h-12 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Completed</p>
              <p className="text-3xl font-bold mt-2">{analytics?.status_distribution?.completed || 0}</p>
            </div>
            <CheckCircle className="w-12 h-12 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-medium">Missed</p>
              <p className="text-3xl font-bold mt-2">{analytics?.status_distribution?.missed || 0}</p>
            </div>
            <XCircle className="w-12 h-12 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Scheduled</p>
              <p className="text-3xl font-bold mt-2">{analytics?.status_distribution?.scheduled || 0}</p>
            </div>
            <Clock className="w-12 h-12 opacity-80" />
          </div>
        </div>
      </div>

      {/* Completion Rate Card */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Completion Rate</h3>
          <TrendingUp className="w-6 h-6 text-green-600" />
        </div>
        <div className="flex items-end">
          <p className="text-5xl font-bold text-gray-900">
            {analytics?.completion_rate?.toFixed(1) || 0}%
          </p>
          <p className="text-gray-600 ml-4 mb-2">of appointments completed</p>
        </div>
        <div className="mt-4 w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-green-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${analytics?.completion_rate || 0}%` }}
          ></div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Appointments Trend */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Appointment Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} name="Total" />
              <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} name="Completed" />
              <Line type="monotone" dataKey="missed" stroke="#ef4444" strokeWidth={2} name="Missed" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Status Distribution Pie Chart */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Appointment Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {statusData.map((status, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: status.color }}></div>
                  <span className="text-sm text-gray-700">{status.name}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">{status.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Peak Hours Chart */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Peak Hours Analysis</h3>
        {peakHoursData.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            No appointment time data available
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={peakHoursData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#8b5cf6" name="Appointments" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Insights Section */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
        <h3 className="text-lg font-semibold mb-3">Appointment Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="opacity-90 mb-1">Completion Rate</p>
            <p className="text-xl font-bold">{analytics?.completion_rate?.toFixed(1) || 0}%</p>
          </div>
          <div>
            <p className="opacity-90 mb-1">Miss Rate</p>
            <p className="text-xl font-bold">
              {analytics?.total_appointments > 0
                ? ((analytics.status_distribution.missed / analytics.total_appointments) * 100).toFixed(1)
                : 0}%
            </p>
          </div>
          <div>
            <p className="opacity-90 mb-1">Cancellation Rate</p>
            <p className="text-xl font-bold">
              {analytics?.total_appointments > 0
                ? ((analytics.status_distribution.cancelled / analytics.total_appointments) * 100).toFixed(1)
                : 0}%
            </p>
          </div>
          <div>
            <p className="opacity-90 mb-1">Show-up Rate</p>
            <p className="text-xl font-bold">
              {analytics?.total_appointments > 0
                ? (((analytics.status_distribution.completed + analytics.status_distribution.scheduled) / analytics.total_appointments) * 100).toFixed(1)
                : 0}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentAnalytics;